import { registerPlugin } from "@capacitor/core";

export type UsageSnapshotDiagnostics = {
  extensionDiagnostic?: string;
  nativeDiagnostic?: string;
  pipelineSummary?: string;
  collectDurationMs?: number;
  pipelineRunId?: string;
  captureSource?: string;
  waitedMs?: number;
  mountedReport?: boolean;
  extensionEmbedded?: boolean;
  attempt?: number;
};

export type UsageSnapshotInput = {
  date: string;
  timezone: string;
  totalSeconds: number;
  unlockCount?: number;
  topApps: Array<{
    appIdentifier?: string;
    displayName: string;
    durationSeconds: number;
    category?: string;
    isEstimated?: boolean;
    color?: string;
    opens?: number;
    /** Sparse per-app hourly buckets (local hour 0..23). Non-zero hours only. */
    hourly?: Array<{ hour: number; seconds: number; opens?: number }>;
  }>;
  apps?: Array<{ packageName?: string; name: string; seconds: number; color?: string; category?: string }>;
  timeBands: Array<{ key: "morning" | "afternoon" | "evening" | "night"; seconds: number; opens?: number }>;
  permission: "granted" | "denied" | "unavailable";
  reason?: string;
} & UsageSnapshotDiagnostics;

export type NativeOAuthPlugin = {
  start(options: { url: string; callbackScheme: string; ephemeral?: boolean }): Promise<{ url: string }>;
  cancel(): Promise<void>;
};

/** Android-only: a natively resolved launcher icon + label for a package. */
export type AndroidAppIcon = {
  packageName: string;
  displayName: string;
  /** base64 PNG data URI, or null when the package is no longer installed. */
  icon: string | null;
};

export type NativeUsageStatsPlugin = {
  getPermissionState(): Promise<{
    status: "granted" | "denied" | "unavailable";
    canOpenSettings: boolean;
    /** Raw AppOps mode for diagnostics ("allowed" | "default" | "ignored" | "errored"). */
    appOpsMode?: string;
    /** APK not installed from an app store (package installer / ADB). */
    isSideloaded?: boolean;
    /** Android 13+ sideload + denied — the toggle is likely blocked behind "Allow restricted settings". */
    restrictedSettingsLikely?: boolean;
  }>;
  openUsageSettings(): Promise<void>;
  /** Opens Screen0's App info page (App info ⋮ → "Allow restricted settings" lives there). */
  openAppInfo?(): Promise<void>;
  collectToday(): Promise<UsageSnapshotInput>;
  /**
   * Collects a completed past calendar day (`date` = YYYY-MM-DD in the device
   * timezone) from UsageStatsManager for history backfill — including days
   * before Screen0 was installed, within OS retention (~7 days). Only present
   * in Android builds from 2026-07-18+ — older APKs reject with "not implemented".
   */
  collectDay(options: { date: string }): Promise<UsageSnapshotInput>;
  /** Android-only: batch-resolve launcher icons + names for the given package names. */
  getAppIcons(options: { packageNames: string[] }): Promise<{ icons: AndroidAppIcon[] }>;
};

export type NativeScreenTimePlugin = {
  getPermissionState(): Promise<{ status: "granted" | "denied" | "notDetermined" | "unavailable"; requiresEntitlement: boolean; reason?: string; rawAuthorizationStatus?: string; hasDataAccess?: boolean }>;
  requestPermission(): Promise<{ status: "granted" | "denied" | "notDetermined" | "unavailable"; requiresEntitlement: boolean; reason?: string; rawAuthorizationStatus?: string; hasDataAccess?: boolean }>;
  collectToday(): Promise<UsageSnapshotInput>;
  /**
   * Collects a full past calendar day (`date` = YYYY-MM-DD in the device
   * timezone) from DeviceActivityData. iOS 26.4+ builds only — older builds
   * reject with "not implemented".
   */
  collectDay(options: { date: string }): Promise<UsageSnapshotInput>;
  /**
   * Presents today's app list in a native DeviceActivityReport sheet. iOS only:
   * real app icons and system-resolved names stay inside Apple's report UI.
   */
  presentTopAppsReport(): Promise<{ presented: boolean; reason?: string }>;
  openSettings(): Promise<void>;
};

export type NativeOnDeviceAIPlugin = {
  getAvailability(): Promise<{ available: boolean; reason?: string; platform: "ios" | "android" }>;
  generateInsight(options: { prompt: string; contextJson: string }): Promise<{
    title: string;
    body: string;
    actions: string[];
  }>;
  chatReply(options: { message: string; contextJson: string; prompt?: string }): Promise<{ reply: string }>;
};

/** Android-only light-impact bridge calibrated for physical, short tap feedback. */
export type NativeAndroidHapticsPlugin = {
  lightImpact(): Promise<void>;
};

/**
 * One rule pushed to the native enforcer. `selectionIds` are the picker
 * selection ids (one per target with a Screen Time selection) the rule shields;
 * the native side resolves them to on-device `ApplicationToken`s and registers a
 * DeviceActivity schedule from `days` + `window`. Optional `suppressedUntil` and
 * `repeats` are read by Swift `applyRules` — keep them in sync
 * (see rulesNativeContract.test.ts).
 */
export type NativeRuleInput = {
  id: string;
  enabled: boolean;
  /** Active weekdays, JS getDay() convention (0=Sun … 6=Sat). */
  days: number[];
  /** Same-day window: startMinute 0–1439, durationMinutes >= 1, no midnight crossing. */
  window: { startMinute: number; durationMinutes: number };
  /** Target selection ids whose picker-selected apps/categories/sites this rule blocks. */
  selectionIds: string[];
  /**
   * Epoch ms; when in the future, native must not shield this rule yet
   * (`isActive` / swipe-to-stop / break). Omitted when unset.
   */
  suppressedUntil?: number;
  /**
   * Recurring DeviceActivity schedule. Quick sessions are one-shot (`false`);
   * scheduled rules default to repeating daily.
   */
  repeats: boolean;
};

/** Display counts for a persisted FamilyActivityPicker selection. */
export type NativeSelectionSummary = {
  selectionId: string;
  applicationCount: number;
  categoryCount: number;
  webDomainCount: number;
};

export type NativeRulesStatus = {
  appBlocking: {
    status: "active" | "unavailable" | "permission_gated" | "entitlement_gated";
    reason?: string;
    enforcedCount?: number;
  };
  webBlocking: {
    status: "active" | "unavailable" | "permission_gated" | "entitlement_gated";
    reason?: string;
    enforcedCount?: number;
  };
};

/** Android-only: an app chosen in the native package picker. */
export type AndroidPickedApp = { packageName: string; displayName: string };

export type NativeRulesPlugin = {
  getStatus(): Promise<NativeRulesStatus>;
  applyRules(options: { rules: NativeRuleInput[] }): Promise<NativeRulesStatus>;
  clearRules(): Promise<NativeRulesStatus>;
  /**
   * iOS: presents the native FamilyActivityPicker and persists the opaque selection
   * under `selectionId`. Android: presents the native package picker and persists the
   * chosen package names under `selectionId`, additionally returning `apps` (real
   * package names + labels) so the web can render icons inline.
   */
  presentAppPicker(options: {
    selectionId: string;
    title?: string;
    headerText?: string;
    footerText?: string;
  }): Promise<NativeSelectionSummary & { canceled: boolean; apps?: AndroidPickedApp[] }>;
  /** Android-only: persists a selection's package set without showing the picker. */
  setSelectionPackages?(options: { selectionId: string; packageNames: string[] }): Promise<NativeSelectionSummary>;
  /** Android-only: opens the "Display over other apps" settings screen. */
  openOverlaySettings?(): Promise<void>;
  /**
   * Presents a read-only native sheet rendering the selection's full list with real
   * Apple icons/names via SwiftUI `Label(token)`.
   */
  presentSelectionDisplay(options: { selectionId: string }): Promise<{ presented: boolean; reason?: string }>;
  /** Presents one read-only sheet with the union of multiple persisted selections. */
  presentCombinedSelectionDisplay(options: {
    selectionIds: string[];
  }): Promise<{ presented: boolean; reason?: string }>;
  /** Reads the persisted selection's display counts (zeros if none stored). */
  getSelectionSummary(options: { selectionId: string }): Promise<NativeSelectionSummary>;
};

/**
 * On-device website filter backed by a local NEPacketTunnelProvider VPN (no remote
 * server — all DNS filtering happens on-device). The Swift `NativeWebFilter` plugin
 * installs/enables/disables the tunnel and writes the blocked-domain set to the
 * `group.com.screen0` app group; the extension drops DNS for those domains so
 * Safari fails to load them. Keep these method names/params in sync with the Swift
 * plugin (see webFilterNativeContract.test.ts).
 */
export type WebFilterStatusValue =
  /** Tunnel installed and running. */
  | "active"
  /** Configuration installed but the tunnel is turned off. */
  | "disabled"
  /** No VPN configuration saved yet. */
  | "not_installed"
  /** User declined the "…wants to add VPN configurations" system prompt. */
  | "denied"
  /** iOS < 16, not iOS, or the Network Extension could not be configured. */
  | "unavailable";

export type WebFilterStatus = {
  status: WebFilterStatusValue;
  installed: boolean;
  enabled: boolean;
  /** Count of domains currently synced to the extension. */
  enforcedCount: number;
  reason?: string;
};

export type NativeWebFilterPlugin = {
  getStatus(): Promise<WebFilterStatus>;
  /** Saves the VPN profile without starting the tunnel (triggers the iOS add-VPN prompt). */
  install(): Promise<WebFilterStatus>;
  /** Installs + enables the VPN profile and starts the tunnel. */
  enable(): Promise<WebFilterStatus>;
  /** Disables the tunnel without removing the saved configuration. */
  disable(): Promise<WebFilterStatus>;
  /** Replaces the blocked-domain set shared with the extension and reapplies it. */
  syncBlockedDomains(options: { domains: string[] }): Promise<WebFilterStatus>;
};

/** App Tracking Transparency status, mirroring `ATTrackingManager.AuthorizationStatus`. */
export type TrackingStatusValue =
  | "notDetermined"
  | "authorized"
  | "denied"
  | "restricted"
  | "unavailable";

export type TrackingStatus = { status: TrackingStatusValue };

export type NativeTrackingPlugin = {
  getStatus(): Promise<TrackingStatus>;
  /** Presents the native ATT prompt (only when status is notDetermined). */
  requestPermission(): Promise<TrackingStatus>;
};

export const NativeOAuth = registerPlugin<NativeOAuthPlugin>("NativeOAuth");
export const NativeUsageStats = registerPlugin<NativeUsageStatsPlugin>("NativeUsageStats");
export const NativeScreenTime = registerPlugin<NativeScreenTimePlugin>("NativeScreenTime");
export const NativeOnDeviceAI = registerPlugin<NativeOnDeviceAIPlugin>("NativeOnDeviceAI");
export const NativeAndroidHaptics = registerPlugin<NativeAndroidHapticsPlugin>("NativeAndroidHaptics");
export const NativeRules = registerPlugin<NativeRulesPlugin>("NativeRules");
export const NativeWebFilter = registerPlugin<NativeWebFilterPlugin>("NativeWebFilter");
export const NativeTracking = registerPlugin<NativeTrackingPlugin>("NativeTracking");
